/**
 * Fase post-«Empezar» para Conversaciones publicadas: chat bifurcado con
 * burbujas NPC (audio karaoke + traducción), opciones del alumno, drawer
 * pedagógico para explicar errores y resumen final con botón de continuar.
 *
 * La intro y el hero los renderiza el padre (`StoryDetailPage`); este
 * componente solo orquesta el chat.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { IconButton } from '@/components/atoms/IconButton';
import { cn } from '@/utils/cn';
import { Play, Pause, Languages } from 'lucide-react';
import { shuffle } from '@/utils/shuffle';
import { useStudyTimeTracker } from '@/hooks/useStudyTimeTracker';
import { usersAPI } from '@/services/api/users.api';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import { InlineKaraokeWords } from '@/components/molecules/KaraokePlayer';
import { SpeedDropdown } from '@/components/molecules/SpeedDropdown';
import { AUDIO_SPEED_OPTIONS } from '@/constants/audio';
import { useNpcLineAudioProgress } from '@/hooks/useNpcLineAudioProgress';
import { buildSyntheticWordTimestamps, parseTranscriptJson } from '@/utils/karaokeTimestamps';
import type { ConversationChatAccuracy } from '@/services/api/conversations.api';
import type {
  ConversationNpcTurn,
  ConversationOption,
  ConversationSituationDocument,
  ConversationStudentTurn,
} from '@/types/conversation.types';
import storyStyles from '@/pages/student/StoryPage/StoryPage.module.scss';
import vocabCardStyles from '@/components/molecules/VocabCard/VocabCard.module.scss';
import styles from './ConversationStartedFlow.module.scss';
import { ConversationWrongExplanationDrawer } from './ConversationWrongExplanationDrawer';

/** Misma señal que Ajustes → «Interfaz» (EN/ES): `i18n.language.startsWith('en')`. */
function explanationForInterfaceLang(
  errorExplanation: string,
  errorExplanationTranslation: string | undefined,
  interfaceEnglish: boolean
): string {
  if (interfaceEnglish) return errorExplanation;
  const es = errorExplanationTranslation?.trim();
  return es && es.length > 0 ? es : errorExplanation;
}

type ChatBubble =
  | {
      kind: 'interlocutor';
      text: string;
      id: string;
      translation?: string;
      audioUrl?: string;
      transcriptJson?: string;
    }
  | {
      kind: 'student';
      text: string;
      id: string;
      wrongExplanation?: { errorExplanation: string; errorExplanationTranslation?: string };
    };

function NpcBubbleContent({
  lineKey,
  text,
  transcriptJson,
  durationSec,
  playingLineKey,
  npcAudioRef,
}: {
  lineKey: string;
  text: string;
  transcriptJson?: string;
  durationSec?: number;
  playingLineKey: string | null;
  npcAudioRef: RefObject<HTMLAudioElement | null>;
}) {
  const { currentTime, isPlaying, hasEnded } = useNpcLineAudioProgress(
    lineKey,
    playingLineKey,
    npcAudioRef
  );

  const timestamps = useMemo(() => {
    const parsed = parseTranscriptJson(transcriptJson);
    if (parsed && parsed.length > 0) return parsed;
    if (!text) return null;
    const dur = durationSec ?? 0;
    if (dur <= 0) return null;
    return buildSyntheticWordTimestamps(text, dur);
  }, [transcriptJson, text, durationSec]);

  if (!text) return null;

  if (timestamps && timestamps.length > 0) {
    return (
      <InlineKaraokeWords
        timestamps={timestamps}
        currentTime={currentTime}
        isPlaying={isPlaying}
        hasEnded={hasEnded}
        className={styles.bubbleKaraokeText}
      />
    );
  }

  return <>{text}</>;
}

function BubbleTranslation({ hint }: { hint?: string }) {
  const t = hint?.trim();
  if (!t) return null;
  return <p className={styles.bubbleHint}>{t}</p>;
}

/** Adapta un turno NPC a la burbuja del chat. */
function npcTurnToBubble(turn: ConversationNpcTurn, bubbleId: string): ChatBubble {
  return {
    kind: 'interlocutor',
    text: turn.text,
    id: bubbleId,
    translation: turn.text_translation,
    audioUrl: turn.audioUrl,
    transcriptJson: turn.transcriptJson,
  };
}

/**
 * Pausas entre burbujas para que la conversación se sienta natural.
 *  - PRE_NPC_REPLY_MS: tras la respuesta del alumno, antes de la del NPC.
 *  - PRE_NEXT_TURN_MS: tras la respuesta correcta del NPC, antes de la siguiente bifurcación.
 *  - INITIAL_NPC_MS: el primer mensaje del NPC al pulsar «Empezar».
 */
const BUBBLE_DELAYS = {
  PRE_NPC_REPLY_MS: 1100,
  PRE_NEXT_TURN_MS: 1300,
  INITIAL_NPC_MS: 650,
} as const;

const BUBBLE_ENTER_DURATION_S = 0.45;

const OPTIONS_EXIT_EASE = [0.16, 0.84, 0.44, 1] as const;
const OPTIONS_EXIT_FADE_S = 0.22;
const OPTIONS_EXIT_COLLAPSE_S = 0.26;
const OPTIONS_EXIT_TOTAL_S = OPTIONS_EXIT_FADE_S + OPTIONS_EXIT_COLLAPSE_S;

const POST_OPTIONS_EXIT_TO_STUDENT_GAP_MS = 320;
const POST_OPTIONS_EXIT_TO_STUDENT_GAP_REDUCED_MS = 140;

export interface ConversationStartedFlowProps {
  document: ConversationSituationDocument;
  hasFollowUpActivities: boolean;
  onContinueToActivities: () => void;
  /** Se llama al finalizar el chat (sin actividades) con la precisión a la 1ª. */
  onFinishWithoutActivities: (accuracy: ConversationChatAccuracy) => Promise<void>;
  /** Segundos de estudio (mismo canal que la biblioteca) para combinar con la fase de actividades en el padre. */
  onStudySecondsChange?: (seconds: number) => void;
}

export function ConversationStartedFlow({
  document: situationDocument,
  hasFollowUpActivities,
  onContinueToActivities,
  onFinishWithoutActivities,
  onStudySecondsChange,
}: ConversationStartedFlowProps) {
  const { t, i18n } = useTranslation();
  const interfaceEnglish = i18n.language.startsWith('en');
  const reducedMotion = Boolean(useReducedMotion());
  const situation = situationDocument.situation;
  const { setContent: setBottomBarContent, setHideFade } = useStoryBottomBar() ?? {};

  const bubbleInitial = (kind: ChatBubble['kind']) =>
    reducedMotion
      ? { opacity: 0 }
      : { opacity: 0, y: 8, x: kind === 'interlocutor' ? -6 : 6, scale: 0.98 };
  const bubbleAnimate = reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, x: 0, scale: 1 };
  const bubbleTransition = {
    duration: BUBBLE_ENTER_DURATION_S,
    ease: [0.16, 0.84, 0.44, 1] as const,
  };

  const optionsContainerVariants = {
    hidden: {},
    show: {
      transition: {
        delayChildren: reducedMotion ? 0 : BUBBLE_ENTER_DURATION_S + 0.05,
        staggerChildren: reducedMotion ? 0 : 0.18,
      },
    },
  };
  const optionItemVariants = reducedMotion
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.22 } },
      }
    : {
        hidden: { opacity: 0, y: 10 },
        show: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.42, ease: [0.16, 0.84, 0.44, 1] as const },
        },
      };

  /**
   * El padre solo monta este componente al pulsar «Empezar», así que el chat
   * está activo desde el primer render. Mantenemos el flag `started` por
   * paralelismo con la biblioteca (timer, scroll suave inicial), pero su
   * valor es siempre `true`.
   */
  const started = true;
  // El tiempo de chat se acumula en `ChallengeResourceStudyDay` (tabla de
  // historias/recursos), NO en la de la biblioteca: esta conversación es una
  // `ConversationPractice`. La racha se marca aparte (ver más abajo).
  const { time: studySeconds } = useStudyTimeTracker(
    started,
    usersAPI.addStoryResourceStudyDelta
  );

  useEffect(() => {
    onStudySecondsChange?.(studySeconds);
  }, [studySeconds, onStudySecondsChange]);

  /** El degradado inferior debe ocultarse durante el chat (igual que en la biblioteca). */
  useEffect(() => {
    setHideFade?.(true);
    return () => setHideFade?.(false);
  }, [setHideFade]);

  const chatStartRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const initialScrollDoneRef = useRef(false);
  const firstScrollSettledRef = useRef(false);

  /** Índice del turno actual dentro de `situation.turns`. Avanza linealmente. */
  const [currentTurnIndex, setCurrentTurnIndex] = useState(0);
  const [messages, setMessages] = useState<ChatBubble[]>([]);
  const [interactionLocked, setInteractionLocked] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [selectedExplanation, setSelectedExplanation] = useState<string | null>(null);
  const [explanationDrawerOpen, setExplanationDrawerOpen] = useState(false);
  const [npcTranslationOpenByBubbleId, setNpcTranslationOpenByBubbleId] = useState<
    Record<string, boolean>
  >({});
  const seeded = useRef(false);
  const markedStreakRef = useRef(false);
  const pendingTimersRef = useRef<Set<number>>(new Set());

  /**
   * Precisión a la primera del chat. `totalStudentTurns` (S) es fijo; cada vez
   * que el alumno resuelve un turno acertando SIN haber fallado antes en ese
   * turno, cuenta como acierto a la primera (F). Un fallo «marca» el turno para
   * que el acierto posterior ya no cuente. Se envía al finalizar para calcular
   * la nota (F/S×100) y el XP proporcional en el backend.
   */
  const totalStudentTurns = useMemo(
    () => situation.turns.filter((turn) => turn.speaker === 'student').length,
    [situation]
  );
  const firstTryCorrectRef = useRef(0);
  const failedTurnIdsRef = useRef<Set<number>>(new Set());
  const buildChatAccuracy = useCallback(
    (): ConversationChatAccuracy => ({
      totalTurns: totalStudentTurns,
      firstTryCorrect: firstTryCorrectRef.current,
    }),
    [totalStudentTurns]
  );

  const scheduleTimeout = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      pendingTimersRef.current.delete(id);
      fn();
    }, ms);
    pendingTimersRef.current.add(id);
    return id;
  }, []);

  useEffect(() => {
    const timersSet = pendingTimersRef.current;
    return () => {
      timersSet.forEach((id) => window.clearTimeout(id));
      timersSet.clear();
    };
  }, []);

  const appendMessages = useCallback((next: ChatBubble[]) => {
    setMessages((prev) => [...prev, ...next]);
  }, []);

  const toggleNpcTranslation = useCallback((bubbleId: string) => {
    setNpcTranslationOpenByBubbleId((prev) => ({
      ...prev,
      [bubbleId]: !prev[bubbleId],
    }));
  }, []);

  /**
   * Cadena NPC pendiente: cuando la burbuja `bubbleId` (la actual o última)
   * termine de reproducirse (audio ended, o por timer si no hay audio), se
   * avanza al turno con índice `nextIndex`. Si `kind === 'summary'`, se cierra
   * la conversación en lugar de avanzar.
   */
  const pendingNpcChainRef = useRef<
    | null
    | { bubbleId: string; kind: 'advance'; nextIndex: number }
    | { bubbleId: string; kind: 'summary' }
  >(null);

  const playingBubbleIdRef = useRef<string | null>(null);

  /**
   * Mientras suena el audio de esta burbuja NPC, el bloque de opciones permanece
   * oculto (`interactionLocked`). Se limpia al `ended` en `handleNpcAudioEnded`.
   */
  const pendingOptionsUnlockBubbleIdRef = useRef<string | null>(null);

  /**
   * Avanza la conversación hasta llegar al siguiente turno del estudiante o al
   * final. Renderiza el turno NPC en `nextIndex` (si existe) y, según haya o
   * no audio, programa el avance al siguiente cuando termine. Si `nextIndex`
   * es un turno student, marca el current y desbloquea opciones. Si está
   * fuera del array, muestra el summary.
   */
  const advanceToTurn = useCallback(
    (nextIndex: number) => {
      const turns = situation.turns;
      if (nextIndex >= turns.length) {
        pendingOptionsUnlockBubbleIdRef.current = null;
        setShowSummary(true);
        setInteractionLocked(false);
        return;
      }
      const next = turns[nextIndex]!;
      setExplanationDrawerOpen(false);

      if (next.speaker === 'student') {
        setCurrentTurnIndex(nextIndex);
        pendingOptionsUnlockBubbleIdRef.current = null;
        setInteractionLocked(false);
        return;
      }

      // NPC turn: render bubble and queue the next advance.
      setCurrentTurnIndex(nextIndex);
      const bubbleId = `npc-turn-${next.id}-${Date.now().toString(36)}`;
      appendMessages([npcTurnToBubble(next, bubbleId)]);

      const followUpIndex = nextIndex + 1;
      if (next.audioUrl) {
        // Si el siguiente turno es student, al terminar el audio desbloqueamos
        // opciones; si es otro NPC (o fin), avanzamos a esa rama.
        if (followUpIndex >= turns.length) {
          pendingNpcChainRef.current = { bubbleId, kind: 'summary' };
        } else if (turns[followUpIndex]!.speaker === 'student') {
          pendingOptionsUnlockBubbleIdRef.current = bubbleId;
          // setCurrentTurnIndex(followUpIndex) lo hará el handler al terminar audio.
          pendingNpcChainRef.current = { bubbleId, kind: 'advance', nextIndex: followUpIndex };
        } else {
          pendingNpcChainRef.current = { bubbleId, kind: 'advance', nextIndex: followUpIndex };
        }
      } else {
        // Sin audio: avanzamos por timer.
        if (followUpIndex >= turns.length) {
          scheduleTimeout(() => {
            setShowSummary(true);
            setInteractionLocked(false);
          }, BUBBLE_DELAYS.PRE_NEXT_TURN_MS);
        } else {
          scheduleTimeout(() => {
            advanceToTurnRef.current(followUpIndex);
          }, BUBBLE_DELAYS.PRE_NEXT_TURN_MS);
        }
      }
    },
    [appendMessages, situation, scheduleTimeout],
  );

  // Self-ref para que advanceToTurn pueda llamarse a sí mismo desde un timer.
  const advanceToTurnRef = useRef<(idx: number) => void>(advanceToTurn);
  useEffect(() => {
    advanceToTurnRef.current = advanceToTurn;
  }, [advanceToTurn]);

  const resolvePendingChainIfInterrupt = useCallback(
    (startingBubbleId: string) => {
      const pending = pendingNpcChainRef.current;
      if (!pending || pending.bubbleId === startingBubbleId) return;
      pendingNpcChainRef.current = null;
      if (pending.kind === 'summary') {
        pendingOptionsUnlockBubbleIdRef.current = null;
        setShowSummary(true);
        setInteractionLocked(false);
      } else {
        advanceToTurn(pending.nextIndex);
      }
    },
    [advanceToTurn],
  );

  const resolveDeferredOptionsUnlockIfInterrupt = useCallback((startingBubbleId: string) => {
    const pendingUnlock = pendingOptionsUnlockBubbleIdRef.current;
    if (!pendingUnlock || pendingUnlock === startingBubbleId) return;
    pendingOptionsUnlockBubbleIdRef.current = null;
    setInteractionLocked(false);
  }, []);

  const handleNpcAudioEndedRef = useRef<() => void>(() => {});

  const handleNpcAudioEnded = useCallback(() => {
    const endedBubbleId = playingBubbleIdRef.current;
    setPlayingLineKey(null);
    playingBubbleIdRef.current = null;

    if (
      pendingOptionsUnlockBubbleIdRef.current !== null &&
      pendingOptionsUnlockBubbleIdRef.current === endedBubbleId
    ) {
      pendingOptionsUnlockBubbleIdRef.current = null;
      setInteractionLocked(false);
    }

    const pending = pendingNpcChainRef.current;
    if (pending && pending.bubbleId === endedBubbleId) {
      pendingNpcChainRef.current = null;

      if (pending.kind === 'summary') {
        pendingOptionsUnlockBubbleIdRef.current = null;
        setShowSummary(true);
        setInteractionLocked(false);
        return;
      }

      scheduleTimeout(() => {
        advanceToTurn(pending.nextIndex);
      }, BUBBLE_DELAYS.PRE_NEXT_TURN_MS);
    }
  }, [advanceToTurn, scheduleTimeout]);

  useEffect(() => {
    handleNpcAudioEndedRef.current = handleNpcAudioEnded;
  }, [handleNpcAudioEnded]);

  const npcAudioRef = useRef<HTMLAudioElement | null>(null);
  const [playingLineKey, setPlayingLineKey] = useState<string | null>(null);
  const [npcAudioTransportPlaying, setNpcAudioTransportPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const playbackSpeedRef = useRef(playbackSpeed);
  const [npcLineDurationByKey, setNpcLineDurationByKey] = useState<Record<string, number>>({});
  const autoplayedBubbleIdsRef = useRef<Set<string>>(new Set());

  const ensureNpcAudio = useCallback(() => {
    if (npcAudioRef.current) return npcAudioRef.current;
    const audio = new Audio();
    const syncTransport = () => {
      setNpcAudioTransportPlaying(!audio.paused && !audio.ended);
    };
    audio.addEventListener('play', syncTransport);
    audio.addEventListener('pause', syncTransport);
    audio.addEventListener('ended', () => {
      setNpcAudioTransportPlaying(false);
      handleNpcAudioEndedRef.current();
    });
    npcAudioRef.current = audio;
    return audio;
  }, []);

  /**
   * Auto-play de la última burbuja NPC con `audioUrl` que aún no se haya
   * intentado reproducir. Usamos el id de la burbuja como `lineKey` para que
   * `useNpcLineAudioProgress` pueda parear la reproducción con la burbuja
   * correcta y el karaoke aparezca solo en esa.
   */
  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (!last || last.kind !== 'interlocutor' || !last.audioUrl) return;
    if (autoplayedBubbleIdsRef.current.has(last.id)) return;
    autoplayedBubbleIdsRef.current.add(last.id);

    const audio = ensureNpcAudio();
    resolvePendingChainIfInterrupt(last.id);
    resolveDeferredOptionsUnlockIfInterrupt(last.id);
    audio.pause();
    audio.src = last.audioUrl;
    audio.playbackRate = playbackSpeedRef.current;
    const lineKey = `npc:${last.id}`;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setPlayingLineKey(lineKey);
          playingBubbleIdRef.current = last.id;
        })
        .catch(() => {
          setPlayingLineKey(null);
        });
    } else {
      setPlayingLineKey(lineKey);
      playingBubbleIdRef.current = last.id;
    }
  }, [messages, ensureNpcAudio, resolvePendingChainIfInterrupt, resolveDeferredOptionsUnlockIfInterrupt]);

  useEffect(() => {
    if (!playingLineKey) return;
    const audio = npcAudioRef.current;
    if (!audio) return;
    const onMeta = () => {
      const d = audio.duration;
      if (d > 0 && !Number.isNaN(d)) {
        setNpcLineDurationByKey((prev) => ({ ...prev, [playingLineKey]: d }));
      }
    };
    onMeta();
    audio.addEventListener('loadedmetadata', onMeta);
    return () => audio.removeEventListener('loadedmetadata', onMeta);
  }, [playingLineKey]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  useEffect(() => {
    const audio = npcAudioRef.current;
    if (audio) audio.playbackRate = playbackSpeed;
  }, [playbackSpeed]);

  useEffect(() => {
    return () => {
      npcAudioRef.current?.pause();
      npcAudioRef.current = null;
    };
  }, []);

  const handleNpcBubbleClick = useCallback(
    (bubbleId: string, audioUrl: string | undefined) => {
      if (!audioUrl) return;
      const audio = ensureNpcAudio();
      const lineKey = `npc:${bubbleId}`;
      resolvePendingChainIfInterrupt(bubbleId);
      resolveDeferredOptionsUnlockIfInterrupt(bubbleId);
      if (playingLineKey === lineKey) {
        if (audio.paused) {
          if (audio.ended) audio.currentTime = 0;
          void audio.play().then(
            () => {
              playingBubbleIdRef.current = bubbleId;
              setPlayingLineKey(lineKey);
            },
            () => undefined
          );
        } else {
          audio.pause();
        }
        return;
      }
      audio.pause();
      audio.src = audioUrl;
      audio.currentTime = 0;
      audio.playbackRate = playbackSpeedRef.current;
      void audio.play().then(
        () => {
          playingBubbleIdRef.current = bubbleId;
          setPlayingLineKey(lineKey);
        },
        () => setPlayingLineKey(null)
      );
    },
    [ensureNpcAudio, playingLineKey, resolvePendingChainIfInterrupt, resolveDeferredOptionsUnlockIfInterrupt]
  );

  const tapStateRef = useRef<{ x: number; y: number; pointerId: number; cancelled: boolean } | null>(
    null
  );
  const TAP_MOVE_THRESHOLD_PX = 10;

  const openExplanationDrawer = useCallback((explanation: string) => {
    setSelectedExplanation(explanation);
    setExplanationDrawerOpen(true);
  }, []);

  const handleWhyWrongPointerDown = useCallback((e: PointerEvent<HTMLButtonElement>) => {
    tapStateRef.current = {
      x: e.clientX,
      y: e.clientY,
      pointerId: e.pointerId,
      cancelled: false,
    };
  }, []);

  const handleWhyWrongPointerMove = useCallback((e: PointerEvent<HTMLButtonElement>) => {
    const state = tapStateRef.current;
    if (!state || state.pointerId !== e.pointerId || state.cancelled) return;
    const dx = Math.abs(e.clientX - state.x);
    const dy = Math.abs(e.clientY - state.y);
    if (dx > TAP_MOVE_THRESHOLD_PX || dy > TAP_MOVE_THRESHOLD_PX) {
      state.cancelled = true;
    }
  }, []);

  const handleWhyWrongPointerCancel = useCallback(() => {
    if (tapStateRef.current) tapStateRef.current.cancelled = true;
  }, []);

  const handleWhyWrongClick = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement>, explanation: string) => {
      const state = tapStateRef.current;
      tapStateRef.current = null;
      if (state?.cancelled) {
        e.preventDefault();
        return;
      }
      openExplanationDrawer(explanation);
    },
    [openExplanationDrawer]
  );

  /** Misma señal de racha que la biblioteca (día con práctica de conversación). */
  useEffect(() => {
    if (!showSummary) {
      markedStreakRef.current = false;
      return;
    }
    if (markedStreakRef.current) return;
    markedStreakRef.current = true;
    usersAPI.markConversationCompleted().catch((err) => {
      console.error('markConversationCompleted failed', err);
    });
  }, [showSummary]);

  /**
   * Scroll one-shot al final cuando aparece el summary. Aislado en su propio
   * efecto con dep única `showSummary`: si lo metiéramos junto al de la barra
   * inferior, cualquier callback no memoizada del padre (p. ej. la flecha
   * inline `onFinishWithoutActivities`) re-ejecutaría el efecto en cada
   * render y volvería a tirar del scroll al fondo, impidiendo que el alumno
   * suba a repasar las burbujas.
   *
   * Esperamos dos `requestAnimationFrame` antes de medir `scrollHeight`: el
   * botón «Finalizar diálogo» se monta en un efecto hermano vía
   * `setBottomBarContent`, lo que cambia el `clientHeight` del scroller. Si
   * lanzamos el `scrollTo` ya, el destino del smooth queda 72 px por encima
   * del fondo real y la última burbuja se ve cortada por el botón.
   */
  useEffect(() => {
    if (!showSummary) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        const scroller = document.querySelector(
          '[data-story-scroll-container]'
        );
        if (scroller) {
          scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
        }
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [showSummary]);

  /** Botón «Finalizar diálogo» / «Continuar a actividades» en la barra inferior. */
  useEffect(() => {
    if (!showSummary) return;
    setBottomBarContent?.(
      <Button
        variant="primary"
        size="lg"
        fullWidth
        className={storyStyles.story__portalButton}
        onClick={() => {
          if (hasFollowUpActivities) {
            onContinueToActivities();
          } else {
            void onFinishWithoutActivities(buildChatAccuracy());
          }
        }}
      >
        {t(hasFollowUpActivities ? 'conversation.continueActivities' : 'conversation.finish')}
      </Button>
    );
    return () => setBottomBarContent?.(null);
  }, [showSummary, hasFollowUpActivities, onContinueToActivities, onFinishWithoutActivities, buildChatAccuracy, setBottomBarContent, t]);

  /** Turno actual: solo lo usamos cuando es del estudiante para renderizar opciones. */
  const currentTurn = situation.turns[currentTurnIndex];
  const currentStudentTurn: ConversationStudentTurn | null =
    currentTurn?.speaker === 'student' ? currentTurn : null;

  const shuffledOptions = useMemo(() => {
    if (!currentStudentTurn) return [];
    if (currentStudentTurn.options.length < 2) return currentStudentTurn.options;
    return shuffle(currentStudentTurn.options);
  }, [currentStudentTurn]);

  /**
   * Primer turno NPC al montar (con delay para no pegar al hero). Si después
   * vienen más turnos NPC consecutivos, la cadena los enlaza al terminar el
   * audio del primero.
   */
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const first = situation.turns[0];
    if (!first || first.speaker !== 'npc') return;
    scheduleTimeout(() => {
      const bubbleId = `npc-turn-${first.id}-init`;
      setMessages([npcTurnToBubble(first, bubbleId)]);
      const followUpIndex = 1;
      const followUp = situation.turns[followUpIndex];
      if (first.audioUrl) {
        if (!followUp) {
          pendingNpcChainRef.current = { bubbleId, kind: 'summary' };
        } else if (followUp.speaker === 'student') {
          pendingOptionsUnlockBubbleIdRef.current = bubbleId;
          pendingNpcChainRef.current = { bubbleId, kind: 'advance', nextIndex: followUpIndex };
        } else {
          pendingNpcChainRef.current = { bubbleId, kind: 'advance', nextIndex: followUpIndex };
        }
      } else {
        // Sin audio en el opening: avanzamos por timer.
        if (!followUp) {
          scheduleTimeout(() => setShowSummary(true), BUBBLE_DELAYS.PRE_NEXT_TURN_MS);
        } else {
          scheduleTimeout(() => {
            advanceToTurnRef.current(followUpIndex);
          }, BUBBLE_DELAYS.PRE_NEXT_TURN_MS);
        }
      }
    }, BUBBLE_DELAYS.INITIAL_NPC_MS);
  }, [situation, scheduleTimeout]);

  /**
   * Primer scroll al montar: sube el intro y deja el `.chat` justo bajo el
   * header sticky. Mismo patrón que la biblioteca, ya validado en producción.
   */
  useEffect(() => {
    if (initialScrollDoneRef.current) return;
    initialScrollDoneRef.current = true;
    const id = window.setTimeout(() => {
      const scroller = document.querySelector(
        '[data-story-scroll-container]'
      );
      if (!scroller) {
        firstScrollSettledRef.current = true;
        return;
      }
      const el = chatStartRef.current;
      if (!el) {
        firstScrollSettledRef.current = true;
        return;
      }
      const elTop = el.getBoundingClientRect().top;
      const scrollerTop = scroller.getBoundingClientRect().top;
      const HEADER_OFFSET = 80;
      const target = scroller.scrollTop + (elTop - scrollerTop) - HEADER_OFFSET;
      scroller.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
      window.setTimeout(() => {
        firstScrollSettledRef.current = true;
      }, 700);
    }, 80);
    return () => window.clearTimeout(id);
  }, []);

  /**
   * Auto-scroll al fondo durante la conversación (estilo chat). Misma lógica
   * exacta que la biblioteca: scroll instantáneo si el alumno ya está pegado
   * al fondo, suave si está dentro de la tolerancia, y nada si está leyendo
   * arriba.
   *
   * Se desactiva en cuanto aparece el summary: ahí el alumno quiere repasar
   * las burbujas y, sobre todo en móvil, el show/hide de la barra de URL
   * cambia `svh` → redimensiona el chat → el observer le re-pegaría al fondo
   * y haría imposible scrollear hacia arriba.
   */
  useEffect(() => {
    if (showSummary) return;
    const chatEl = chatRef.current;
    if (!chatEl) return;
    const scroller = document.querySelector(
      '[data-story-scroll-container]'
    );
    if (!scroller) return;
    if (typeof ResizeObserver === 'undefined') return;
    const STICK_TOLERANCE_PX = 360;
    const STICKY_NEAR_BOTTOM_PX = 60;
    let firstObserverCallSkipped = false;
    const observer = new ResizeObserver(() => {
      if (!firstObserverCallSkipped) {
        firstObserverCallSkipped = true;
        return;
      }
      if (!firstScrollSettledRef.current) return;
      const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (distanceFromBottom <= 0) return;
      if (distanceFromBottom <= STICKY_NEAR_BOTTOM_PX || reducedMotion) {
        scroller.scrollTop = scroller.scrollHeight;
        return;
      }
      if (distanceFromBottom <= STICK_TOLERANCE_PX) {
        scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
      }
    });
    observer.observe(chatEl);
    return () => observer.disconnect();
  }, [reducedMotion, showSummary]);

  useEffect(() => {
    if (!explanationDrawerOpen) return;
    if (selectedExplanation !== null) return;
    setExplanationDrawerOpen(false);
  }, [explanationDrawerOpen, selectedExplanation]);

  const handlePickOption = (opt: ConversationOption) => {
    if (!currentStudentTurn || interactionLocked || showSummary) return;
    setExplanationDrawerOpen(false);
    setInteractionLocked(true);

    const turnId = currentStudentTurn.id;
    const turnIndex = currentTurnIndex;

    // Precisión a la 1ª: acierto sin fallo previo en este turno = suma F.
    // Un fallo marca el turno para que el acierto posterior ya no cuente.
    if (opt.correct) {
      if (!failedTurnIdsRef.current.has(turnId)) {
        firstTryCorrectRef.current += 1;
      }
    } else {
      failedTurnIdsRef.current.add(turnId);
    }

    const studentBubbleId = `stu-${turnId}-${opt.id}-${Date.now().toString(36)}`;
    const studentBubble: ChatBubble = opt.correct
      ? {
          kind: 'student',
          text: opt.text,
          id: studentBubbleId,
        }
      : {
          kind: 'student',
          text: opt.text,
          id: studentBubbleId,
          wrongExplanation: {
            errorExplanation: opt.error_explanation,
            errorExplanationTranslation: opt.error_explanation_translation,
          },
        };

    const optionsExitDoneMs = reducedMotion ? 220 : Math.round(OPTIONS_EXIT_TOTAL_S * 1000);
    const studentBubbleDelayMs =
      optionsExitDoneMs +
      (reducedMotion ? POST_OPTIONS_EXIT_TO_STUDENT_GAP_REDUCED_MS : POST_OPTIONS_EXIT_TO_STUDENT_GAP_MS);

    scheduleTimeout(() => {
      appendMessages([studentBubble]);

      if (opt.correct) {
        // Camino correcto: avanzamos al siguiente turno (que será NPC, normalmente)
        // tras una pausa natural. La burbuja NPC se renderiza dentro de advanceToTurn.
        scheduleTimeout(() => {
          advanceToTurn(turnIndex + 1);
        }, BUBBLE_DELAYS.PRE_NPC_REPLY_MS);
        return;
      }

      // Camino incorrecto: el NPC reacciona al error y volvemos al mismo turno
      // student para que el alumno reintente.
      scheduleTimeout(() => {
        const reactionBubbleId = `npc-react-${turnId}-${opt.id}-${Date.now().toString(36)}`;
        appendMessages([
          {
            kind: 'interlocutor',
            text: opt.interlocutor_reaction,
            id: reactionBubbleId,
            translation: opt.interlocutor_reaction_translation,
            audioUrl: opt.interlocutor_reaction_audioUrl,
            transcriptJson: opt.interlocutor_reaction_transcriptJson,
          },
        ]);
        if (opt.interlocutor_reaction_audioUrl) {
          pendingOptionsUnlockBubbleIdRef.current = reactionBubbleId;
        } else {
          setInteractionLocked(false);
        }
      }, BUBBLE_DELAYS.PRE_NPC_REPLY_MS);
    }, studentBubbleDelayMs);
  };

  return (
    <>
      <div
        ref={chatRef}
        className={cn(styles.chat, showSummary && styles.chatSummary)}
        aria-live="polite"
      >
        <div ref={chatStartRef} aria-hidden />

        {messages.map((m) => (
          <Fragment key={m.id}>
            <motion.div
              className={cn(styles.row, m.kind === 'interlocutor' ? styles.rowNpc : styles.rowYou)}
              initial={bubbleInitial(m.kind)}
              animate={bubbleAnimate}
              transition={bubbleTransition}
            >
              {m.kind === 'interlocutor' ? (
                <div className={styles.npcBubbleGroup}>
                  {m.audioUrl ? (
                    <div className={cn(styles.bubble, styles.bubbleNpc, styles.bubbleNpcAudioRow)}>
                      <button
                        type="button"
                        className={vocabCardStyles['vocabCard__playBtn']}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNpcBubbleClick(m.id, m.audioUrl);
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        aria-label={
                          playingLineKey === `npc:${m.id}` && npcAudioTransportPlaying
                            ? t('activities.pausePronunciation')
                            : t('activities.playPronunciation')
                        }
                      >
                        <Icon
                          icon={
                            playingLineKey === `npc:${m.id}` && npcAudioTransportPlaying ? Pause : Play
                          }
                          size="sm"
                          aria-hidden
                        />
                      </button>
                      <div className={styles.bubbleNpcTextCol}>
                        <NpcBubbleContent
                          lineKey={`npc:${m.id}`}
                          text={m.text}
                          transcriptJson={m.transcriptJson}
                          durationSec={npcLineDurationByKey[`npc:${m.id}`]}
                          playingLineKey={playingLineKey}
                          npcAudioRef={npcAudioRef}
                        />
                        {!!npcTranslationOpenByBubbleId[m.id] && (
                          <BubbleTranslation hint={m.translation} />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={cn(styles.bubble, styles.bubbleNpc)}>
                      <NpcBubbleContent
                        lineKey={`npc:${m.id}`}
                        text={m.text}
                        transcriptJson={m.transcriptJson}
                        durationSec={npcLineDurationByKey[`npc:${m.id}`]}
                        playingLineKey={playingLineKey}
                        npcAudioRef={npcAudioRef}
                      />
                      {!!npcTranslationOpenByBubbleId[m.id] && (
                        <BubbleTranslation hint={m.translation} />
                      )}
                    </div>
                  )}
                  {(m.translation?.trim() || m.audioUrl) && (
                    <div className={styles.bubbleActions}>
                      {m.translation?.trim() && (
                        <IconButton
                          icon={Languages}
                          size="sm"
                          className={styles.bubbleTranslationToggle}
                          aria-label={
                            npcTranslationOpenByBubbleId[m.id]
                              ? t('conversation.hideTranslation')
                              : t('conversation.showTranslation')
                          }
                          aria-expanded={!!npcTranslationOpenByBubbleId[m.id]}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleNpcTranslation(m.id);
                          }}
                        />
                      )}
                      {m.audioUrl && (
                        <SpeedDropdown
                          speed={playbackSpeed}
                          onSpeedChange={setPlaybackSpeed}
                          options={AUDIO_SPEED_OPTIONS}
                        />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className={cn(styles.bubble, styles.bubbleYou)}>{m.text}</div>
              )}
            </motion.div>
            {!showSummary && m.kind === 'student' && m.wrongExplanation && (
              <motion.div
                className={styles.whyWrongRow}
                initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
                animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{
                  duration: 0.36,
                  delay: BUBBLE_ENTER_DURATION_S * 0.5,
                  ease: [0.16, 0.84, 0.44, 1],
                }}
              >
                <button
                  type="button"
                  className={styles.whyWrongLink}
                  data-no-press-scale=""
                  onPointerDown={handleWhyWrongPointerDown}
                  onPointerMove={handleWhyWrongPointerMove}
                  onPointerCancel={handleWhyWrongPointerCancel}
                  onClick={(e) =>
                    handleWhyWrongClick(
                      e,
                      explanationForInterfaceLang(
                        m.wrongExplanation!.errorExplanation,
                        m.wrongExplanation!.errorExplanationTranslation,
                        interfaceEnglish
                      )
                    )
                  }
                >
                  {t('conversation.whyWrong')}
                </button>
              </motion.div>
            )}
          </Fragment>
        ))}

        <AnimatePresence mode="wait" initial={false}>
          {!showSummary &&
            currentStudentTurn &&
            messages.length > 0 &&
            !interactionLocked && (
              <motion.div
                key={currentStudentTurn.id}
                className={styles.optionsWrapper}
                initial={false}
                exit={
                  reducedMotion
                    ? { opacity: 0, transition: { duration: 0.22 } }
                    : {
                        opacity: 0,
                        y: 10,
                        height: 0,
                        transition: {
                          opacity: { duration: OPTIONS_EXIT_FADE_S, ease: OPTIONS_EXIT_EASE },
                          y: { duration: OPTIONS_EXIT_FADE_S, ease: OPTIONS_EXIT_EASE },
                          height: {
                            duration: OPTIONS_EXIT_COLLAPSE_S,
                            delay: OPTIONS_EXIT_FADE_S,
                            ease: OPTIONS_EXIT_EASE,
                          },
                        },
                      }
                }
              >
                <motion.div
                  className={styles.options}
                  variants={optionsContainerVariants}
                  initial="hidden"
                  animate="show"
                >
                  <motion.p className={styles.optionsPrompt} variants={optionItemVariants}>
                    {t('conversation.chooseBestAnswer')}
                  </motion.p>
                  {shuffledOptions.map((opt) => (
                    <motion.div
                      key={`${currentStudentTurn.id}-${opt.id}`}
                      className={styles.optionItem}
                      variants={optionItemVariants}
                    >
                      <Button
                        type="button"
                        variant="secondary"
                        className={styles.optionBtn}
                        onClick={() => handlePickOption(opt)}
                      >
                        {opt.text}
                      </Button>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}
        </AnimatePresence>

        <div ref={chatEndRef} aria-hidden />
      </div>

      <ConversationWrongExplanationDrawer
        open={explanationDrawerOpen}
        onOpenChange={setExplanationDrawerOpen}
        explanation={selectedExplanation}
      />
    </>
  );
}
